import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { TextField, Toggle, Section } from '@/admin/AdminUI';
import { Plus, Trash2, ChevronUp, ChevronDown } from 'lucide-react';

export default function FAQTab() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const r = await api.get('/admin/faq');
    setItems(r.data || []);
  };
  useEffect(() => { load(); }, []);

  const addNew = () => setEditing({ question: '', answer: '', visible: true, order: items.length + 1 });
  const startEdit = (it) => setEditing({ ...it });

  const remove = async (id) => {
    if (!window.confirm('Delete this FAQ entry?')) return;
    await api.delete(`/admin/faq/${id}`);
    load();
  };

  const move = async (id, dir) => {
    const idx = items.findIndex((i) => i.id === id);
    const swap = idx + dir;
    if (swap < 0 || swap >= items.length) return;
    const next = [...items];
    [next[idx], next[swap]] = [next[swap], next[idx]];
    setItems(next);
    await api.put('/admin/faq/reorder', { items: next.map((it, i) => ({ id: it.id, order: i + 1 })) });
    load();
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        question: editing.question || '',
        answer: editing.answer || '',
        visible: editing.visible !== false,
        order: typeof editing.order === 'number' ? editing.order : items.length + 1,
      };
      if (editing.id) {
        await api.put(`/admin/faq/${editing.id}`, payload);
      } else {
        await api.post('/admin/faq', payload);
      }
      setEditing(null);
      load();
    } finally { setSaving(false); }
  };

  const toggleVisible = async (it) => {
    const payload = {
      question: it.question || '',
      answer: it.answer || '',
      visible: !it.visible,
      order: it.order || 0,
    };
    await api.put(`/admin/faq/${it.id}`, payload);
    load();
  };

  return (
    <div>
      <Section
        title="FAQ Entries"
        action={
          <button onClick={addNew} className="btn-primary text-[12px] !py-2.5 !px-4" data-testid="add-faq">
            <Plus size={14} className="mr-1" /> Add Entry
          </button>
        }
      >
        <p className="font-body text-[12px] text-ink-muted mb-4">
          Entries render in the homepage FAQ accordion in the order shown below. Toggle visibility per entry.
        </p>
        <div className="space-y-2.5">
          {items.map((it, idx) => (
            <div
              key={it.id}
              className="flex items-center gap-3 p-3 border border-border-line rounded-sm bg-white"
              data-testid="faq-row-admin"
            >
              <span className="font-display font-bold text-[12px] text-ink-soft w-6 text-center">{it.order}</span>
              <div className="flex-1 min-w-0">
                <div className="font-display font-bold uppercase text-ink-primary text-[13px] tracking-tightish truncate">{it.question}</div>
                <div className="font-body text-[11px] text-ink-muted line-clamp-1">{it.answer}</div>
              </div>
              <span className="font-body text-[11px] uppercase tracking-wider2 text-ink-soft">
                {it.visible !== false ? 'Visible' : 'Hidden'}
              </span>
              <div className="flex flex-col gap-1">
                <button onClick={() => move(it.id, -1)} disabled={idx === 0} className="p-1 disabled:opacity-30"><ChevronUp size={14} /></button>
                <button onClick={() => move(it.id, 1)} disabled={idx === items.length - 1} className="p-1 disabled:opacity-30"><ChevronDown size={14} /></button>
              </div>
              <button onClick={() => toggleVisible(it)} className="btn-secondary !py-2 !px-3 text-[11px]" data-testid="faq-toggle-visible">
                {it.visible !== false ? 'Hide' : 'Show'}
              </button>
              <button onClick={() => startEdit(it)} className="btn-secondary !py-2 !px-3 text-[11px]">Edit</button>
              <button onClick={() => remove(it.id)} className="btn-secondary !py-2 !px-3 text-[11px]"><Trash2 size={14} /></button>
            </div>
          ))}
          {items.length === 0 && (
            <p className="font-body text-[12px] text-ink-muted py-4">No FAQ entries yet.</p>
          )}
        </div>
      </Section>

      {editing && (
        <div
          className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center px-4"
          onClick={() => setEditing(null)}
        >
          <div
            className="bg-bg-elevated max-w-2xl w-full rounded-lg p-6 max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="font-display font-bold uppercase text-[18px] mb-4">
              {editing.id ? 'Edit FAQ Entry' : 'New FAQ Entry'}
            </h3>
            <div className="space-y-4">
              <TextField
                label="Question"
                value={editing.question}
                onChange={(v) => setEditing({ ...editing, question: v })}
                testid="faq-edit-question"
              />
              <div>
                <label className="block">
                  <span className="block font-body text-[12px] font-semibold uppercase text-ink-muted tracking-wider2 mb-1.5">Answer</span>
                  <textarea
                    value={editing.answer || ''}
                    onChange={(e) => setEditing({ ...editing, answer: e.target.value })}
                    rows={8}
                    className="w-full border border-border-line rounded-sm px-3 py-2 font-body text-[14px] bg-white focus:outline-none focus:border-ink-primary"
                    data-testid="faq-edit-answer"
                  />
                </label>
                <p className="mt-1 font-body text-[11px] text-ink-muted">
                  For bulleted lists, start each item on a new line with "- " (e.g., "- First item"). Email addresses auto-render as orange links.
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <label className="block">
                  <span className="block font-body text-[12px] font-semibold uppercase text-ink-muted tracking-wider2 mb-1.5">Order</span>
                  <input
                    type="number"
                    value={editing.order ?? 0}
                    onChange={(e) => setEditing({ ...editing, order: parseInt(e.target.value, 10) || 0 })}
                    className="w-full border border-border-line rounded-sm px-3 py-2 font-body text-[14px] bg-white focus:outline-none focus:border-ink-primary"
                    data-testid="faq-edit-order"
                  />
                </label>
                <div className="pt-6 md:pt-7">
                  <Toggle
                    label="Visible on site"
                    value={editing.visible !== false}
                    onChange={(v) => setEditing({ ...editing, visible: v })}
                    testid="faq-edit-visible"
                  />
                </div>
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <button onClick={() => setEditing(null)} className="btn-secondary text-[12px] !py-2 !px-4">Cancel</button>
              <button onClick={save} disabled={saving} className="btn-primary text-[12px] !py-2 !px-4" data-testid="faq-edit-save">
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
